import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatCents, discountPercent } from "@/lib/format";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import sixPack from "@/assets/product-six-pack-white.jpg";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/shop")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("shop")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Shop Glow-in-the-Dark Golf Balls — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Browse 6-packs, 12-packs, starter 3-packs, and event packs of glow-in-the-dark golf balls. Six colors per pack." },
      { property: "og:title", content: loaderData?.title || "Shop Glow Long Golf Balls" },
      { property: "og:description", content: loaderData?.meta_description || "6-packs, 12-packs, starter packs, and event packs of glow golf balls." },
      { property: "og:url", content: "https://glowlonggolf.com/shop" },
      { property: "og:image", content: SHARE_IMAGES.fairwayArc },
      { name: "twitter:image", content: SHARE_IMAGES.fairwayArc },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/shop" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: ShopPage,
});

function ShopPage() {
  const { data: variants } = useQuery({
    queryKey: ["all-variants"],
    queryFn: async () => {
      const { data } = await supabase
        .from("product_variants")
        .select("id,name,pack_size,price_cents,compare_at_cents,badge,sku,products!inner(slug,title,short_description)")
        .eq("is_active", true)
        .order("sort_order");
      return data ?? [];
    },
  });

  return (
    <EditablePageShell slug="shop" defaults={{ eyebrow: "Shop", title: "Pick your pack", lead: "Every pack ships with one of each glowing color: red, green, pink, white, blue, orange." }}>
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(variants ?? []).map((v: any) => {
            const off = discountPercent(v.price_cents, v.compare_at_cents);
            return (
              <Link
                key={v.id}
                to="/products/$slug"
                params={{ slug: v.products.slug }}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-glow"
              >
                {v.badge && (
                  <span className="absolute left-3 top-3 z-10 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">
                    {v.badge}
                  </span>
                )}
                <div className="aspect-square overflow-hidden bg-background">
                  <img src={sixPack} alt={v.name} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-display text-lg font-semibold">{v.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{v.pack_size} balls · one of each color (multiples in larger packs)</p>
                  <div className="mt-auto flex items-baseline gap-2 pt-4">
                    <span className="font-display text-xl font-semibold">{formatCents(v.price_cents)}</span>
                    {v.compare_at_cents && (
                      <>
                        <span className="text-sm text-muted-foreground line-through">{formatCents(v.compare_at_cents)}</span>
                        {off && <span className="text-xs font-semibold text-primary">−{off}%</span>}
                      </>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </EditablePageShell>
  );
}
